import React, { useEffect, useState } from 'react';
import { styled } from '@compiled/react';
import Heading from '@atlaskit/heading';
import OpenIcon from '@atlaskit/icon/glyph/open';
import Spinner from '@atlaskit/spinner';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { getIdFromAri } from '@atlassian/jira-compass-common/src/common/utils.tsx';
import type { OutwardRelationships, InwardRelationships } from '../../../../../common/model/types';
import { getTeams } from '../../../../../common/rest/teams';
import DependencyRow from './depency-row';

type Props = {
	testId?: string;
	componentId: string;
	dependsOn: OutwardRelationships;
	dependedOnBy: InwardRelationships;
};

export default function PopupContent(props: Props) {
	const { testId, componentId, dependsOn, dependedOnBy } = props;
	const [teamNamesById, setTeamNamesById] = useState<Record<string, string>>({});
	const [teamsLoading, setTeamsLoading] = useState(true);

	useEffect(() => {
		const uniqueOwnerIds = Array.from(
			new Set([
				...dependsOn.nodes.flatMap((node) =>
					node.endNode.ownerId ? [getIdFromAri(node.endNode.ownerId)] : [],
				),
				...dependedOnBy.nodes.flatMap((node) =>
					node.startNode.ownerId ? [getIdFromAri(node.startNode.ownerId)] : [],
				),
			]),
		);

		if (uniqueOwnerIds.length > 0) {
			getTeams(uniqueOwnerIds)
				.then((teams) => {
					setTeamNamesById(
						teams.teams.reduce<Record<string, string>>(
							(acc, curTeam) => Object.assign(acc, { [curTeam.id]: curTeam.displayName }),
							{},
						),
					);
				})
				.catch(() => setTeamNamesById({}))
				.finally(() => setTeamsLoading(false));
		} else {
			setTeamsLoading(false);
		}
	}, [dependsOn, dependedOnBy]);

	if (teamsLoading) {
		return (
			<ContentWrapper data-testid={testId}>
				<Spinner size="large" />
			</ContentWrapper>
		);
	}

	return (
		<ContentWrapper data-testid={testId}>
			{dependsOn.nodes.length > 0 && (
				<Section>
					<HeadingWrapper>
						<Heading level="h200">Depends on</Heading>
					</HeadingWrapper>
					{dependsOn.nodes.map((node, idx) => (
						<DependencyRow
							key={idx}
							node={{ ...node.endNode }}
							ownerName={teamNamesById[getIdFromAri(node.endNode.ownerId ?? '')]}
						/>
					))}
					{dependsOn.pageInfo.hasNextPage && <ViewMoreLink componentId={componentId} />}
				</Section>
			)}

			{dependedOnBy.nodes.length > 0 && (
				<Section>
					<HeadingWrapper>
						<Heading level="h200">Depended on by</Heading>
					</HeadingWrapper>
					{dependedOnBy.nodes.map((node, idx) => (
						<DependencyRow
							key={idx}
							node={{ ...node.startNode }}
							ownerName={teamNamesById[getIdFromAri(node.startNode.ownerId ?? '')]}
						/>
					))}
					{dependedOnBy.pageInfo.hasNextPage && <ViewMoreLink componentId={componentId} />}
				</Section>
			)}
		</ContentWrapper>
	);
}

const ViewMoreLink = ({ componentId }: { componentId: string }) => (
	<Link
		href={`/compass/component/${componentId}/dependencies`}
		target="_blank"
		rel="noopener noreferrer"
	>
		View more <OpenIcon label="" size="small" />
	</Link>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Section = styled.div({
	marginBottom: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingWrapper = styled.div({
	marginBottom: token('space.150', '12px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		color: token('color.text', colors.N900),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div({
	width: 432,
	height: '100%',
	padding: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Link = styled.a({
	display: 'flex',
	width: 'fit-content',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		marginLeft: token('space.050', '4px'),
	},
});
